import React, { useId } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import type {
  OrgRunControlType,
  OrgRunDatePreset,
  OrgRunStatusGroup,
  OrgRunTestType,
} from '../../../types/controlTestRun.ts';
import {
  CONTROL_TYPE_FILTER_OPTIONS,
  DATE_RANGE_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TEST_TYPE_FILTER_OPTIONS,
  type FilterOption,
} from '../constants.ts';

export interface FiltersProps {
  searchQuery: string;
  datePreset: OrgRunDatePreset;
  statusFilter: OrgRunStatusGroup;
  testTypeFilter: OrgRunTestType;
  controlTypeFilter: OrgRunControlType;
  /** When omitted, search input is read-only / no-op (avoid crashes if parent forgets to wire). */
  onSearchChange?: (v: string) => void;
  onDatePresetChange: (v: OrgRunDatePreset) => void;
  onStatusChange: (v: OrgRunStatusGroup) => void;
  onTestTypeChange: (v: OrgRunTestType) => void;
  onControlTypeChange: (v: OrgRunControlType) => void;
  onReset: () => void;
}

type SelectFilterProps<V extends string> = {
  label: string;
  value: V;
  options: readonly FilterOption<V>[];
  onChange: (v: V) => void;
};

const SelectFilter = <V extends string,>({ label, value, options, onChange }: SelectFilterProps<V>): React.ReactElement => {
  const id = useId();
  return (
  <div className="flex-1 min-w-[140px]">
    <label className="block text-xs mb-2 text-gray-600" htmlFor={id}>{label}</label>
    <select
      id={id}
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value as V)}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
  );
};

export const Filters: React.FC<FiltersProps> = ({
  searchQuery,
  datePreset,
  statusFilter,
  testTypeFilter,
  controlTypeFilter,
  onSearchChange,
  onDatePresetChange,
  onStatusChange,
  onTestTypeChange,
  onControlTypeChange,
  onReset,
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
    <div className="flex flex-wrap items-end gap-4">
      <div className="flex-[2] min-w-[220px]">
        <label className="block text-xs mb-2 text-gray-600" htmlFor="test-runs-search">
          Search
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            id="test-runs-search"
            type="search"
            placeholder="Control ID, control title or test name"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoComplete="off"
          />
        </div>
      </div>
      <SelectFilter label="Status" value={statusFilter} options={STATUS_FILTER_OPTIONS} onChange={onStatusChange} />
      <SelectFilter label="Test type" value={testTypeFilter} options={TEST_TYPE_FILTER_OPTIONS} onChange={onTestTypeChange} />
      <SelectFilter label="Control type" value={controlTypeFilter} options={CONTROL_TYPE_FILTER_OPTIONS} onChange={onControlTypeChange} />
      <SelectFilter label="Created" value={datePreset} options={DATE_RANGE_OPTIONS} onChange={onDatePresetChange} />
      <div className="flex items-end">
        <button
          type="button"
          onClick={onReset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
      </div>
    </div>
  </div>
);
